import styles from './styles.module.scss';
import { WarrantyLogFileDto } from 'api-new/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useWarrantyLogDeleteFileMutation, useWarrantyLogUploadFileMutation } from 'entities/warranty';
import { formatErrorMessage, QueryError } from 'features/shared/model';
import useFetchPdf from 'hooks/use-fetch-pdf';
import useIsMounted from 'hooks/use-is-mounted';
import { ChangeEvent, FC, memo, useEffect, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { toast } from 'react-toastify';
import { Button } from 'shared';

const SCROLLBAR_HEIGHT = '120px';

type Props = {
  id?: number;
  files?: WarrantyLogFileDto[] | null;
};

export const UploadPdf: FC<Props> = memo(function UploadPdf({ id, files }: Props) {
  const [localFiles, setLocalFiles] = useState<WarrantyLogFileDto[]>();
  const inputFile = useRef<HTMLInputElement>(null);
  const fetchPdf = useFetchPdf();
  const isMounted = useIsMounted();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  useEffect(() => {
    setLocalFiles(files?.length ? [...files] : []);
  }, [files]);

  const { mutate: deleteFile, isLoading: isLoadingDeleteFile } = useWarrantyLogDeleteFileMutation();
  const { mutate: uploadFile, isLoading: isLoadingUploadFile } = useWarrantyLogUploadFileMutation();

  const handleUploadClick = () => {
    inputFile?.current?.click();
  };

  const onError = (error: QueryError) => {
    const message = formatErrorMessage(error);
    toast.error(message);
    console.error(message);
  };

  const handleDelete = (fileId: string) => {
    if (!id || !confirm('Delete file?')) return;

    deleteFile(
      {
        fileUid: fileId,
        logId: id!
      },
      {
        onSuccess: (data) => {
          if (!data) return;
          toast.success('File uploaded successfully!');
          const newArray = localFiles?.filter((item) => item.id != fileId);

          if (!newArray?.length) return;

          setLocalFiles(newArray);
        },
        onError: onError
      }
    );
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !id) return;

    uploadFile(
      {
        logId: id!,
        file: e.target.files[0]
      },
      {
        onSuccess: (data) => {
          setLocalFiles(localFiles?.length ? [...localFiles, data] : [data]);
          e.target.value = '';
        },
        onError: onError
      }
    );
  };

  const previewDocHandler = (url?: string | null) => {
    if (!url?.length) return;

    setIsDownloading(true);
    fetchPdf(url)
      .then((response) => {
        if (response) {
          const file = new Blob([response.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        isMounted() && setIsDownloading(false);
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>Upload PDF</div>
      <PerfectScrollbar style={{ height: SCROLLBAR_HEIGHT, paddingRight: '10px', marginBottom: '10px', width: '100%' }}>
        <div className="table-responsive">
          <table className={clsx(styles.table, 'table')}>
            <tbody>
              {localFiles?.map((x, i) => (
                <tr key={x.id}>
                  <td className="w-100">File #{i + 1}.pdf</td>
                  <td>
                    <div>
                      <button
                        type="button"
                        className={styles.openItemBtn}
                        disabled={isDownloading}
                        onClick={() => previewDocHandler(x.fileUrl)}
                      >
                        Open
                        <InlineSvg path="/media/icons/custom/cif-open.svg" className="ms-1" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.deleteItemBtn}
                      disabled={isLoadingDeleteFile || isLoadingUploadFile}
                      onClick={() => handleDelete(x.id!)}
                    >
                      <InlineSvg path="/media/icons/custom/close-new-vehicle-settings.svg" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PerfectScrollbar>
      <div className={styles.footer}>
        <input
          type="file"
          multiple={false}
          ref={inputFile}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="image/png,image/jpeg,image/jpg,application/pdf"
        />
        <Button
          text="Attach PDF file"
          onClick={handleUploadClick}
          variant="light-secondary"
          size="sm"
          icon={<InlineSvg path="/media/icons/custom/minus-squared.svg" />}
          isLoading={isLoadingUploadFile}
          disabled={isLoadingDeleteFile}
        />
      </div>
    </div>
  );
});
